import { FloatingLinkUrlInput, type LinkFloatingToolbarState,LinkOpenButton, useFloatingLinkEdit, useFloatingLinkEditState, useFloatingLinkInsert, useFloatingLinkInsertState } from '@alpod-ai-writer/plate-plugins/link/react';
import { cn } from '@udecode/cn';
import { useFormInputProps } from '@udecode/plate/react';
import {
    flip,
    offset,
    type UseVirtualFloatingOptions,
} from '@udecode/plate-floating';
import { ExternalLink, Link, Link2OffIcon, Text } from 'lucide-react';

import { buttonVariants } from '../common/button.component';
import { inputVariants } from '../common/input.component';
import { popoverVariants } from '../common/popover.component';
import { Separator } from '../common/separator.component';
import { TooltipButton } from '../common/tooltip.component';

const floatingOptions: UseVirtualFloatingOptions = {
    middleware: [
        offset(12),
        flip({
            fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
            padding: 12,
        }),
    ],
    placement: 'bottom-start',
};

export interface LinkFloatingToolbarProps {
  state?: LinkFloatingToolbarState;
}

export function LinkFloatingToolbar({ state }: LinkFloatingToolbarProps) {
    const insertState = useFloatingLinkInsertState({
        ...state,
        floatingOptions: {
            ...floatingOptions,
            ...state?.floatingOptions,
        },
    });

    const {
        hidden,
        props: insertProps,
        ref: insertRef,
        textInputProps,
    } = useFloatingLinkInsert(insertState);

    const editState = useFloatingLinkEditState({
        ...state,
        floatingOptions: {
            ...floatingOptions,
            ...state?.floatingOptions,
        },
    });

    const {
        editButtonProps,
        props: editProps,
        ref: editRef,
        unlinkButtonProps,
    } = useFloatingLinkEdit(editState);

    const inputProps = useFormInputProps({
        preventDefaultOnEnterKeydown: true        
    });

    if (hidden) return null;

    const input = (
        <div className="flex w-[330px] flex-col" {...inputProps}>
            <div className="flex items-center">
                <div className="flex items-center pr-1 pl-2 text-gray-800">
                    <Link className="size-4" />
                </div>
                <FloatingLinkUrlInput
                    className={inputVariants({ h: 'sm', variant: 'ghost' })}
                    placeholder="Vložte odkaz"
                    data-plate-focus
                />
            </div>
            <Separator className="my-1" />
            <div className="flex items-center">
                <div className="flex items-center pr-1 pl-2 text-gray-800">
                    <Text className="size-4" />
                </div>
                <input
                    className={inputVariants({ h: 'sm', variant: 'ghost' })}
                    placeholder="Vložte zobrazený text"
                    data-plate-focus
                    {...textInputProps}
                />
            </div>
        </div>
    );

    const editContent = editState.isEditing ? (
        input
    ) : (
        <div className="box-content flex items-center">
            <button
                className={buttonVariants({ size: 'sm', variant: 'ghost' })}
                type="button"
                {...editButtonProps}
            >
                Upravit odkaz
            </button>
            <Separator orientation="vertical" />
            <LinkOpenButton
                className={buttonVariants({
                    size: 'icon',
                    variant: 'ghost',
                })}
            >
                <ExternalLink width={18} />
            </LinkOpenButton>
            <Separator orientation="vertical" />
            <TooltipButton
                className={buttonVariants({
                    size: 'icon',
                    variant: 'ghost',
                })}
                type="button"
                {...unlinkButtonProps}
                tooltip="Zrušit odkaz"
            >
                <Link2OffIcon width={18} />
            </TooltipButton>
        </div>
    );

    return (
        <>
            <div
                ref={insertRef}
                className={cn(popoverVariants(), 'w-auto p-1')}
                {...insertProps}
            >
                {input}
            </div>
            <div
                ref={editRef}
                className={cn(popoverVariants(), 'w-auto p-1')}
                {...editProps}
            >
                {editContent}
            </div>
        </>
    );
}
